import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../../i18n/i18n';
import { Typography } from 'antd';
import './InstallationLocationDetailInfo.scss';
import { userApi } from '../../../api/user.api';
import DataItem from './DataItem';
import EditInstallationLocationModal, { UpdateLocationParams } from './EditInstallationLocationModal';
import useAuthStore from '../../../stores/auth.store';

export interface InstallationLocationDetailInfoProps {
  uuid: string;
  erpId: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
  statusColor: string;
  statusName: string;
  numInstallations: string;
  numDevices: string;
  latitude: string;
  longitude: string;
  tenantUuid: string;
  adminUuid?: string;
  onSave: (values: UpdateLocationParams) => Promise<void>;
}

const { Title } = Typography;
const InstallationLocationDetailInfo = (props: InstallationLocationDetailInfoProps) => {
  const { t } = useTranslation('installation-location', { keyPrefix: 'installationLocationDetail' });
  const isSuperAdminOrCompanyAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdmin);
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const userUuid = useAuthStore((state) => state.uuid);
  const isAdminOfLocation = isAdmin && userUuid === props.adminUuid;

  const [adminName, setAdminName] = useState<string>('-');

  useEffect(() => {
    if (!props.adminUuid) {
      setAdminName('-');
      return;
    }
    let active = true;
    userApi
      .getUserByUuid(props.adminUuid)
      .then((user) => {
        if (active) setAdminName(user.name);
      })
      .catch(() => {
        if (active) setAdminName('-');
      });
    return () => {
      active = false;
    };
  }, [props.adminUuid]);

  return (
    <div className={'InstallationLocationDetailPage__generalData'}>
      <div className={'InstallationLocationDetailPage__generalInfo__titleArea'}>
        <Title level={4} className={'InstallationLocationDetailPage__generalData__title'}>
          {t('generalData')}
        </Title>

        {(isSuperAdminOrCompanyAdmin || isAdminOfLocation) && (
          <EditInstallationLocationModal
            uuid={props.uuid}
            name={props.name}
            erpId={props.erpId}
            address={props.address}
            postalCode={props.postalCode}
            city={props.city}
            latitude={props.latitude}
            longitude={props.longitude}
            tenantUuid={props.tenantUuid}
            adminUuid={props.adminUuid}
            adminName={adminName === '-' ? undefined : adminName}
            onSave={props.onSave}
          />
        )}
      </div>
      <div className={'InstallationLocationDetailPage__generalData__rows'}>
        <div className={'InstallationLocationDetailPage__generalData__rows__column'}>
          <DataItem title={t('address')} data={props.address} />
          <DataItem title={t('postalCode')} data={props.postalCode} />

          <DataItem title={t('poblation')} data={props.city} />
        </div>
        <div className={'InstallationLocationDetailPage__generalData__rows__column'}>
          <DataItem title={t('numDevices')} data={props.numDevices} />
          <DataItem title={t('numInstallationLocation')} data={props.numInstallations} />

          <DataItem
            title={t('coordinates')}
            data={`${props.latitude && !isNaN(parseFloat(props.latitude)) ? parseFloat(props.latitude).toFixed(6) : '-'}º N ${props.longitude && !isNaN(parseFloat(props.longitude)) ? parseFloat(props.longitude).toFixed(6) : '-'}º E`}
          />
        </div>
        <div className={'InstallationLocationDetailPage__generalData__rows__column'}>
          <DataItem title={t('status')} data={props.statusName} status={true} statusColor={props.statusColor} />
          <DataItem title={t('admin')} data={adminName} />
          <DataItem title={`${t('reference')} ${props.name}`} data={props.erpId} />
        </div>
      </div>
    </div>
  );
};

export default InstallationLocationDetailInfo;
